import React, { useState } from 'react'
import { useS4LContext } from '@s4l/react-context'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import { Collapsible, Form, MessageBox, Typography } from '@s4l/ds'

import RequestList from './RequestList'

import styles from './styles.module.css'

export interface SpecialRequest {
  requestKey?: string
  requestValue: string
  datetime?: number
}

export interface ISpecialRequestsProps {
  specialRequests: SpecialRequest[]
  hash: string
}

type RequestState = 'idle' | 'loading' | 'success' | 'error'

function SpecialRequests({ specialRequests = [], hash }: Readonly<ISpecialRequestsProps>) {
  const { i18n, domain } = useS4LContext()
  const [textAreaValue, setTextAreaValue] = useState<string>('')
  const [addedRequests, setAddedRequests] = useState<SpecialRequest[]>([])
  const [requestState, setRequestState] = useState<RequestState>('idle')

  async function handleSubmit(): Promise<void> {
    if (!textAreaValue.trim()) return

    setRequestState('loading')

    const [error] = await domain.CreateSpecialRequestBookingUseCase.execute({
      bookingHash: hash,
      specialRequest: textAreaValue,
    })

    if (error) {
      setRequestState('error')
      return
    }

    setAddedRequests(prev => [...prev, { requestValue: textAreaValue, datetime: Date.now() }])
    setTextAreaValue('')
    setRequestState('success')
  }

  const isLoading = requestState === 'loading'
  const showSuccess = requestState === 'success'
  const showError = requestState === 'error'

  return (
    <div className={styles.specialRequests}>
      <Typography
        className="mb-16"
        variant="title"
        size="s"
        as="h5"
        color="var(--color-neutral-800)"
      >
        {i18n.formatMessage({ id: 'room.special-requests' })}
      </Typography>

      <RequestList specialRequests={[...specialRequests, ...addedRequests]} />

      <Collapsible title={i18n.formatMessage({ id: 'special-requests' })} defaultOpen={false}>
        <Form onSubmit={handleSubmit}>
          <div className={styles.addSpecialRequest}>
            <Typography variant="body" size="l" as="span">
              {i18n.formatMessage({ id: 'checkout.special-requests-advice' })}
            </Typography>
            <Form.TextArea
              name="special-request"
              placeholder={i18n.formatMessage({ id: 'room.special-requests' })}
              defaultValue={textAreaValue}
              onChange={setTextAreaValue}
            />
            <Form.SubmitButton isLoading={isLoading} disabled={textAreaValue.trim() === ''}>
              {i18n.formatMessage({ id: 'global.send-request' })}
            </Form.SubmitButton>
          </div>
        </Form>
      </Collapsible>

      {showSuccess && (
        <div className={styles.feedbackSection} data-testid="status-ok">
          <MessageBox kind="success">
            <MessageBox.Title>
              {i18n.formatMessage({ id: 'room.special-requests-confirmation' })}
            </MessageBox.Title>
          </MessageBox>
        </div>
      )}

      {showError && (
        <div className={styles.feedbackSection} data-testid="status-not-ok">
          <MessageBox kind="error">
            <MessageBox.Title>
              {i18n.formatMessage({ id: 'room.special-requests-notok' })}
            </MessageBox.Title>
          </MessageBox>
        </div>
      )}
    </div>
  )
}

SpecialRequests.displayName = 'SpecialRequests'

export default withErrorBoundary(SpecialRequests)
